import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import VisitClientSelect from "./visit-client-select";
import VisitClient from "./visit-client";
import VisitsClientAbons from "./visits-client-abons";
import { Button, Checkbox, Form, Input, InputNumber, Select, message, Popconfirm } from "antd";
import { serverFetch } from "../../server";
import SaleAdd from "../sales/sale-add";
import { CloseOutlined } from "@ant-design/icons";
import { isMobile } from "../../extensions/design";
import { dictsGetUsers, dictsDictionary2Options, userOptionsWithPosition } from "../../extensions/dicts";
import VisitCardSelect from "./visit-card-select";

const VisitClientAdd = ({ onClose, defaultClientId, defaultSaleId, defaultSaleStatus, defaultSaleProductId, defaultPacketUseServiceChecked, defaultExecutorUserId, abon }) => {
  const [clientId, setClientId] = useState(defaultClientId);
  const [item, setItem] = useState(defaultSaleId ? { saleId: defaultSaleId, status: defaultSaleStatus ?? 0 } : undefined);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saleAdd, setSaleAdd] = useState(false);
  // Признак «Списать услугу» по умолчанию: явно переданный флаг либо параметр номенклатуры абонемента
  const defaultUseService = !!defaultPacketUseServiceChecked || !!abon?.saleUseOnVisit;
  // Принудительный флаг от вызывающего кода действует только для исходно переданного абонемента:
  // после выбора другого абонемента признак пересчитывается по его параметрам
  const initialSaleIdRef = useRef(defaultSaleId);
  // Актуальный выбранный абонемент - для игнорирования ответов по устаревшим запросам
  const itemSaleIdRef = useRef(item?.saleId);
  itemSaleIdRef.current = item?.saleId;
  const [packetUseServiceChecked, setPacketUseServiceChecked] = useState(defaultUseService);
  const [packetUseServiceForced, setPacketUseServiceForced] = useState(defaultUseService);
  const [packetUseServiceLoading, setPacketUseServiceLoading] = useState(false);
  // Группа клиента - для переключения между членами группы
  const [familyList, setFamilyList] = useState([]);
  // Представитель группы (корень группы)
  const [familyRootId, setFamilyRootId] = useState(null);
  // Услуги пакета выбранного абонемента (чекбокс и селектор отображаются только при наличии пакетов)
  const [packets, setPackets] = useState([]);
  const prevSaleIdRef = useRef(null); 
  const clientAbonsRef = useRef();
  // Исполнитель списаний: тренер абонемента, при отсутствии - тренер клиента с карточки
  const [executorUserId, setExecutorUserId] = useState(defaultExecutorUserId ?? abon?.userId ?? null);
  // Выбранная услуга пакета для списания
  const [selectedPacketProductId, setSelectedPacketProductId] = useState(defaultSaleProductId ?? null);
  // Количество списаний
  const [packetUseQty, setPacketUseQty] = useState(1);
  // Исполнитель списания
  const [packetUseUserId, setPacketUseUserId] = useState(defaultExecutorUserId ?? null);
  const packetUseUserIdRef = useRef(packetUseUserId);
  packetUseUserIdRef.current = packetUseUserId;
  // Исполнители для выбранной услуги пакета
  const [executorUsers, setExecutorUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Загружаем группу: в ней всегда есть представитель, поэтому можно вернуться от участника группы к родителю и переключиться на другого участника группы
  useEffect(() => {
    if (!clientId) {
      setFamilyList([]);
      setFamilyRootId(null);
      return;
    }
    serverFetch(`client/${clientId}`)
      .then((client) => {
        const rootId = client?.representativeId || client.id;
        return serverFetch(`client/${rootId}/family`)
          .then((children) => ({ rootId, client, children: Array.isArray(children) ? children : [] }));
      })
      .then(({ rootId, client, children }) => {
        if (rootId === client.id) {
          setFamilyRootId(rootId);
          setFamilyList([client, ...children]);
          return;
        }
        // Корень - представитель, добавляем и его данные в список
        return serverFetch(`client/${rootId}`)
          .then((root) => {
            setFamilyRootId(rootId);
            setFamilyList([root, ...children]);
          });
      })
      .catch(() => {
        setFamilyRootId(null);
        setFamilyList([]);
      });
  }, [clientId]);

  // При смене клиента через выпадающий список группы: если у нового клиента уже есть открытый визит - открываем его
  const handleClientChange = (newClientId) => {
    setClientId(newClientId);
    serverFetch(`visit/Client/${newClientId}?isOpen=true`)
      .then(visits => {
        const openVisit = Array.isArray(visits) ? visits.find(v => !v.finish) : null;
        if (openVisit) {
          onClose(true, openVisit.id ?? openVisit.Id);
        }
      })
      .catch(() => {});
  };

  // Выбранные услуги и пакет принадлежат конкретному клиенту, поэтому при переключении между членами группы их нужно сбросить.
  const prevClientIdRef = useRef(clientId);
  useEffect(() => {
    if (prevClientIdRef.current === clientId) return;
    prevClientIdRef.current = clientId;
    setItem(prev => (prev?.saleId ? { ...prev, saleId: null } : prev));
  }, [clientId]);

  // Загружаем услуги пакета выбранного абонемента
  useEffect(() => {
    if (!clientId || !item?.saleId) {
      setPackets([]);
      setSelectedPacketProductId(null);
      prevSaleIdRef.current = null;
      return;
    }

    const currentSaleId = item.saleId;
    const isNewSale = prevSaleIdRef.current !== currentSaleId;
    prevSaleIdRef.current = currentSaleId;

    serverFetch(`sale/ProductList/${currentSaleId}`)
      .then(data => {
        // Игнорируем устаревшие ответы: пользователь мог выбрать другой абонемент, пока шел запрос
        if (currentSaleId !== itemSaleIdRef.current) return;

        const list = Array.isArray(data) ? data : [];
        const available = list.filter(p => (p.qtyRest || 0) > 0);
        setPackets(available);
        
        // Умный выбор услуги
        setSelectedPacketProductId(prev => {
          if (available.length === 0) return null;

          // Если это первичное открытие модалки, пытаемся использовать переданный defaultSaleProductId
          if (isNewSale && currentSaleId === initialSaleIdRef.current && defaultSaleProductId) {
            if (available.some(p => p.id === defaultSaleProductId)) return defaultSaleProductId;
          }

          // При смене абонемента всегда берем первую услугу
          if (isNewSale) return available[0].id;

          // Если абонемент не менялся, сохраняем выбор, если услуга еще в списке
          if (available.some(p => p.id === prev)) return prev;

          return available[0].id;
        });
      })
      .catch(() => {
        if (currentSaleId === itemSaleIdRef.current) {
          setPackets([]);
          setSelectedPacketProductId(null);
        }
      });
  }, [clientId, item?.saleId, item?.status]);

  // Следим, чтобы количество не превышало остаток при смене услуги
  useEffect(() => {
    if (selectedPacketProductId) {
      const currentPacket = packets.find(p => p.id === selectedPacketProductId);
      const maxQty = currentPacket?.qtyRest || 1;
      if (packetUseQty > maxQty) {
        setPacketUseQty(maxQty);
      }
    }
  }, [selectedPacketProductId, packets, packetUseQty]);

  // Загружаем исполнителей для выбранной услуги пакета
  useEffect(() => {
    if (!selectedPacketProductId) {
      setExecutorUsers([]);
      return;
    }
    const product = packets.find(p => p.id === selectedPacketProductId);
    if (!product?.productId) {
      setExecutorUsers([]);
      return;
    }
    setUsersLoading(true);
    serverFetch(`User/ProductUsersAsUsers/${product.productId}`)
      .then(data => {
        if (data && data.length > 0) {
          setExecutorUsers(dictsDictionary2Options(data));
        } else {
          dictsGetUsers(setExecutorUsers, { activeOnly: true });
        }
        setUsersLoading(false);
      })
      .catch(() => {
        setExecutorUsers([]);
        setUsersLoading(false);
      });
  }, [selectedPacketProductId, packets]);

  // Если у нас появился дефолтный исполнитель (из абонемента или карточки), подставляем его
  useEffect(() => {
    setPacketUseUserId(executorUserId ?? null);
  }, [executorUserId]);

  // Исполнитель по умолчанию (тренер абонемента) может отсутствовать среди исполнителей услуги.
  // Подгружаем такого сотрудника отдельным запросом и добавляем в начало списка недоступной для выбора опцией.
  useEffect(() => {
    if (packetUseUserId == null) return;
    if (executorUsers.some(u => u.value === packetUseUserId)) return;
    serverFetch(`User/${packetUseUserId}`)
      .then(u => {
        // Игнорируем устаревшие ответы: исполнитель мог смениться, пока запрос был в полёте
        if (!u || u.id == null || u.id !== packetUseUserIdRef.current) return;
        // Не дублируем: пользователь мог появиться в актуальном списке, пока запрос был в полёте
        setExecutorUsers(prev => prev.some(x => x.value === u.id)
          ? prev
          : [{ ...u, value: u.id, label: u.name, disabled: true }, ...prev]);
      })
      .catch(() => {});
  }, [packetUseUserId, executorUsers]);

  const onValuesChange = value => {
    if ('cardCode' in value) {
      setItem(prev => ({...prev, ...value, keyName: value.cardCode ? prev.keyName : ''}));
      if (!value.cardCode) {
        form.setFieldValue('keyName', '');
      }
    } else {
      setItem({...item, ...value});
    }
  }

  const onSave = () => {
    setLoading(true);
    // userId — клиентское поле (тренер абонемента для исполнителя списаний),
    // API визита его не принимает: Visit.UserId заполняется из контекста авторизации,
    // поэтому null в теле запроса ронял бы сериализацию
    const { userId: _, ...visit } = item ?? {};
    serverFetch("visit", { method: "POST", bodyData: visit })
      .then(data => {
        const visitId = data.id;
        // Добавляем списание выбранной услуги пакета после открытия визита
        if (packetUseServiceChecked && selectedPacketProductId) {
          return serverFetch("sale/Use", {
            method: "POST",
            bodyData: {
              saleId: item.saleId,
              saleProductId: selectedPacketProductId,
              visitId: visitId,
              userId: packetUseUserId,
              qty: packetUseQty,
            },
          }).then(() => data);
        }
        return data;
      })
      .then(data => onClose(true, data.id))
      .catch(e => {
        message.error(e.userMessage);
        setLoading(false);
      })
  } 

  const onSaleAddClose = ( refresh ) => {
    setSaleAdd(false);
    refresh && clientAbonsRef.current.getItems();
  }

  const onSaleActive = () => {
    setLoading(true);
    const saleId = item?.saleId;
    serverFetch("sale/SetActivated", {method: "POST", bodyData: {saleId}})
      .then(() => {
        setLoading(false);
        return serverFetch(`sale/${saleId}`);
      })
      .then(sale => {
        setItem(prev => ({ ...prev, ...sale, status: sale?.status ?? prev?.status ?? 2 }));
        clientAbonsRef.current.getItems();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  const onSaleUnfreeze = () => {
    setLoading(true);
    const saleId = item?.saleId;
    serverFetch("sale/SetUnfrozen", {method: "POST", bodyData: {saleId}})
      .then(() => {
        setLoading(false);
        return serverFetch(`sale/${saleId}`);
      })
      .then(sale => {
        setItem(prev => ({ ...prev, ...sale, status: sale?.status ?? prev?.status ?? 2 }));
        clientAbonsRef.current.getItems();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  useEffect(() => {
    if (!item?.saleId) {
      setPacketUseServiceChecked(false);
      setPacketUseServiceForced(false);
      setPacketUseServiceLoading(false);
      return;
    }
    const saleId = item.saleId;
    // Флаг, принудительно заданный вызывающим кодом, применяем только к исходному абонементу
    const forcedDefault = defaultUseService && saleId === initialSaleIdRef.current;
    // Признак уже известен из выбранной продажи/абонемента - не переопределяем
    if (forcedDefault || item?.saleUseOnVisit) {
      setPacketUseServiceChecked(true);
      setPacketUseServiceForced(true);
      setPacketUseServiceLoading(false);
      return;
    }
    setPacketUseServiceLoading(true);
    serverFetch(`sale/${saleId}`)
      .then(sale => {
        // Игнорируем ответ по устаревшему абонементу: пользователь успел выбрать другой
        if (saleId !== itemSaleIdRef.current) return undefined;
        if (sale?.productId) {
          setItem(prev => ({ ...prev, status: sale?.status ?? prev?.status ?? 0 }));
        }
        // Тренер абонемента становится исполнителем списаний
        if (sale?.userId != null) setExecutorUserId(sale.userId);
        // Признак по умолчанию задан вызывающим кодом или абонементом - не переопределяем
        if (forcedDefault || sale?.saleUseOnVisit) {
          setPacketUseServiceChecked(true);
          setPacketUseServiceForced(true);
          setPacketUseServiceLoading(false);
          return undefined;
        }
        if (!sale?.productId) {
          setPacketUseServiceChecked(false);
          setPacketUseServiceForced(false);
          setPacketUseServiceLoading(false);
          return undefined;
        }
        return serverFetch(`product/${sale.productId}`)
          .then(product => {
            // Игнорируем ответ по устаревшему абонементу
            if (saleId !== itemSaleIdRef.current) return undefined;
            const forced = !!product?.saleUseOnVisit;
            setPacketUseServiceChecked(forced);
            setPacketUseServiceForced(forced);
            setPacketUseServiceLoading(false);
          })
          .catch(() => {
            setPacketUseServiceChecked(false);
            setPacketUseServiceForced(false);
            setPacketUseServiceLoading(false);
          });
      })
      .catch(() => {
        setPacketUseServiceChecked(false);
        setPacketUseServiceForced(false);
        setPacketUseServiceLoading(false);
      });
  }, [item?.saleId, item?.saleUseOnVisit, item?.status, defaultUseService]);

  // Если тренер не определён абонементом - берём поле «Тренер» с карточки клиента
  useEffect(() => {
    if (!clientId || executorUserId != null) return;
    serverFetch(`client/${clientId}`)
      .then(data => {
        if (data?.userId != null) setExecutorUserId(data.userId);
      })
      .catch(() => {});
  }, [clientId, executorUserId]);

  // Проверяем, выбран ли исполнитель и услуга, если стоит галочка "Списать услугу"
  const isExecutorMissing = packetUseServiceChecked && (!selectedPacketProductId || !packetUseUserId);

  const divButtons = isMobile ? <div className="visit-client-add-buttons record-form-item-with-margin-top-mobile">
    {loading ? null : <Button disabled={loading} onClick={() => setSaleAdd(true)}>Продать</Button>}
    {!item?.saleId || loading || (item?.status !== 1 && item?.status !== 0) ? null : <Button onClick={onSaleActive}>Активировать</Button>}
    {!item?.saleId || loading || item?.status !== 3 ? null : 
      <Popconfirm title="Подтверждение" description="Разморозить абонемент?" onConfirm={onSaleUnfreeze} placement="top" getPopupContainer={(trigger) => trigger.closest('.visit-client-add-buttons record-form-item-with-margin-top-mobile')} overlayClassName="centered-popconfirm">
        <Button>Разморозить</Button>
      </Popconfirm>}
    {!item?.saleId || loading || item?.status !== 2 ? null : <Button onClick={onSave} disabled={isExecutorMissing}>Открыть визит</Button>}
  </div> : <>
    <Form.Item label=" ">
      <div className="main-content">
        <Button disabled={loading} onClick={() => setSaleAdd(true)}>Продать</Button>
        <Button disabled={!item?.saleId || loading || (item?.status !== 1 && item?.status !== 0)} onClick={onSaleActive}>Активировать</Button>
        <Popconfirm title="Подтверждение" description="Разморозить абонемент?" onConfirm={onSaleUnfreeze}>
          <Button disabled={!item?.saleId || loading || item?.status !== 3}>Разморозить</Button>
        </Popconfirm>
        <Button onClick={onSave} disabled={!item?.saleId || loading || item?.status !== 2 || isExecutorMissing}>Открыть визит</Button>
        <Button onClick={() => onClose(false)} disabled={!item?.saleId || loading}>Отмена</Button>
      </div>
    </Form.Item>
  </>

  const modalContent = (
    <div className="sport-modal">
      <div className="sport-modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose(false)}>
        <div className={!isMobile ? "sport-modal-content" : "sport-modal-content mobile-content" } style={isMobile ? {} : {width: 900}} onMouseDown={e => e.stopPropagation()}>
          <div className="sport-modal-title">
            <span>Визит клиента</span>
            <CloseOutlined className="sport-section-title-close" onClick={onClose} style={{ zIndex: 9 }}   />
          </div>
      {!clientId && <VisitClientSelect onSelect={setClientId} onCancel={() => onClose(false)}/>}
      {clientId && <>
        <VisitClient
          id={clientId}
          family={familyList}
          rootId={familyRootId}
          onClientChange={handleClientChange}
          onRefresh={() => clientAbonsRef.current.getItems()}
        />
        <VisitsClientAbons
          clientId={clientId}
          onSelect={sale => {
            // Используем prev, чтобы не затереть другие изменения
            setItem(prev => ({ ...prev, saleId: sale?.id, status: sale?.status, saleUseOnVisit: sale?.saleUseOnVisit, userId: sale?.userId }));
            // При смене абонемента подставляем его тренера
            setExecutorUserId(sale?.userId ?? null);
          }}
          saleId={item?.saleId}
          ref={clientAbonsRef}
        />
        <Form layout={ isMobile? "inline" : "" } form={form} onValuesChange={onValuesChange} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }} colon={false} disabled={!item?.saleId}>
          <Form.Item className={ isMobile ? "records-form-item-mobile" : ""} label="Ключ" name="keyName" wrapperCol={!isMobile ? {span: 3} : {}}>
            <Input className={ isMobile ? "records-form-item-mobile" : ""} disabled={!!item?.cardCode} />
          </Form.Item>
          <Form.Item className={ isMobile ? "record-form-item-with-margin-top-mobile" : ""} label="Доп. карта" name="cardCode" wrapperCol={!isMobile ? {span: 12} : {}}>
            <VisitCardSelect disabled={!item?.saleId || loading} onCardData={card => form.setFieldValue('keyName', card?.name ?? '')} />
          </Form.Item>
          <Form.Item className={ isMobile ? "record-form-item-with-margin-top-mobile" : ""} label="Коммент." name="comment" wrapperCol={!isMobile ? {span: 16} : {}}>
            <Input className={ isMobile ? "records-form-item-mobile" : ""}/>
          </Form.Item>
          {item?.saleId && clientId && isMobile && packets.length > 0 && (
            <div style={{ marginBottom: 24, width: "100%", display: "block" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, marginBottom: packetUseServiceChecked ? 12 : 0 }}>
              <span style={{ fontWeight: 500 }}>Списать услугу</span>
              <Checkbox
                checked={packetUseServiceChecked}
                onChange={e => setPacketUseServiceChecked(e.target.checked)}
                disabled={packetUseServiceLoading || packetUseServiceForced}
              />
            </div>
            {packetUseServiceChecked && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontWeight: 500, whiteSpace: "nowrap", minWidth: 90 }}>Услуга:</span>
                  <Select
                    className="records-form-item-mobile"
                    popupClassName="mobile-select-dropdown"
                    style={{ flex: 1, minWidth: 0}}
                    value={selectedPacketProductId}
                    onChange={value => setSelectedPacketProductId(value)}
                    options={packets.map(p => ({
                      value: p.id,
                      label: `${p.productName} (остаток: ${p.qtyRest || 0} / ${p.qty || 0})`,
                    }))}
                    placeholder="Выберите услугу"
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontWeight: 500, whiteSpace: "nowrap", minWidth: 90 }}>Количество:</span>
                  <InputNumber
                    className="records-form-item-mobile"
                    min={1}
                    max={packets.find(p => p.id === selectedPacketProductId)?.qtyRest || 1}
                    value={packetUseQty}
                    onChange={value => setPacketUseQty(value ?? 1)}
                    style={{ width: 80 }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 500, whiteSpace: "nowrap", minWidth: 90 }}>Исполнитель:</span>
                  <Select
                    className="records-form-item-mobile"
                    popupClassName="mobile-select-dropdown"
                    style={{ flex: 1, minWidth: 0 }}
                    value={packetUseUserId}
                    onChange={value => setPacketUseUserId(value)}
                    options={userOptionsWithPosition(executorUsers)}
                    optionLabelProp="title"
                    placeholder="Выберите исполнителя"
                    loading={usersLoading}
                  />
                </div>
              </>
            )}
          </div>
          )}
          
          {item?.saleId && clientId && !isMobile && packets.length > 0 && (
          <Form.Item
            label={
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, whiteSpace: "nowrap" }}>
                <span>Списать услугу</span>
                <Checkbox
                  checked={packetUseServiceChecked}
                  onChange={e => setPacketUseServiceChecked(e.target.checked)}
                  disabled={packetUseServiceLoading || packetUseServiceForced}
                />
              </div>
            }
            style={{ marginBottom: 16 }}
          >
            {packetUseServiceChecked && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginLeft: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ whiteSpace: "nowrap" }}>Услуга:</span>
                  <Select
                    style={{ flex: 1, minWidth: 0 }}
                    value={selectedPacketProductId}
                    onChange={value => setSelectedPacketProductId(value)}
                    options={packets.map(p => ({
                      value: p.id,
                      label: `${p.productName} (остаток: ${p.qtyRest || 0} / ${p.qty || 0})`,
                    }))}
                    placeholder="Выберите услугу"
                  />
                  <span style={{ whiteSpace: "nowrap", marginLeft: 8 }}>Количество:</span>
                  <InputNumber
                    min={1}
                    max={packets.find(p => p.id === selectedPacketProductId)?.qtyRest || 1}
                    value={packetUseQty}
                    onChange={value => setPacketUseQty(value ?? 1)}
                    style={{ width: 80, flexShrink: 0 }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ whiteSpace: "nowrap" }}>Исполнитель:</span>
                  <Select
                    style={{ flex: 1, minWidth: 0 }}
                    value={packetUseUserId}
                    onChange={value => setPacketUseUserId(value)}
                    options={userOptionsWithPosition(executorUsers)}
                    optionLabelProp="title"
                    placeholder="Выберите исполнителя"
                    loading={usersLoading}
                  />
                    </div>
                </div>
              )}
            </Form.Item>
          )}
          {divButtons}
        </Form>
        {saleAdd && <SaleAdd onClose={onSaleAddClose} clientId={clientId} activeBtns={["Абонемент", "Пакет"]} />}
      </>}
    </div>
    </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default VisitClientAdd;